import originSrc from '@/shared/assets/images/origin.svg';
import { ScaleIcon } from '@/shared/ui/Icons/ScaleIcon';
import { useSidePanelScroll } from '@/shared/ui/SidePanelWrapper';
import { Badge, Button, Separator } from '@monksflow/monks-ui';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MousePointerClickIcon,
  RocketIcon,
  TargetIcon,
  UsersIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { SECTION_CLASS_NAMES } from './TerritoryDetail.constants';
import type { TerritoryDetailProps, SectionProps, SimpleCardProps } from './TerritoryDetail.types';
import { useTranslation } from 'react-i18next';

export function TerritoryDetail(props: TerritoryDetailProps) {
  const { t } = useTranslation('analysis');

  const { territory, exporting } = props;
  // The chrome-less print page has no side-panel context and no navigation.
  const displayNavigation = exporting ? false : props.displayNavigation;
  const { scrollToTop } = useSidePanelScroll();

  useEffect(() => {
    if (exporting) return;
    scrollToTop();
  }, [scrollToTop, exporting]);

  return (
    <div className={exporting ? 'px-8' : ''}>
      <div className="flex justify-between pt-8">
        <div className="flex gap-2">
          <Badge variant="secondary">
            {t('strategicTerritoriesWidget.territoryDetail.territory')}
          </Badge>
          {territory.isRecommended && (
            <Badge variant="secondary">
              {t('strategicTerritoriesWidget.territoryDetail.recommended')}
            </Badge>
          )}
        </div>

        {displayNavigation && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label={t('strategicTerritoriesWidget.territoryDetail.previousTerritory')}
              onClick={props.onPrevious}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('strategicTerritoriesWidget.territoryDetail.nextTerritory')}
              onClick={props.onNext}
            >
              <ChevronRightIcon />
            </Button>
          </div>
        )}
      </div>

      {/* Title and Summary */}
      <div className={`flex flex-col gap-4 ${SECTION_CLASS_NAMES}`}>
        <div className="flex items-center gap-4">
          <p className="wrap-break-word text-heading-xl text-foreground">{territory.title}</p>
        </div>

        <p className="text-body-lg text-muted-foreground">{territory.summary}</p>
      </div>

      <Section
        title={
          <>
            <MousePointerClickIcon />
            {t('strategicTerritoriesWidget.territoryDetail.keyMessage')}
          </>
        }
      >
        <div className="rounded-3xl bg-orange-50 p-6">
          <p className="text-heading-md text-foreground">{territory.keyMessage}</p>
        </div>
      </Section>

      <Section
        title={
          <>
            <img src={originSrc} width={24} height={24} alt="" />
            {t('strategicTerritoriesWidget.territoryDetail.coreTensions')}
          </>
        }
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {territory.coreTensions.map((item) => (
            <SimpleCard key={item.title} title={item.title} description={item.description} />
          ))}
        </div>
      </Section>

      <Section
        title={
          <>
            <TargetIcon />
            {t('strategicTerritoriesWidget.territoryDetail.getWhoToBy')}
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4">
          {territory.strategicBrief.map((item) => (
            <SimpleCard key={item.title} title={item.title} description={item.description} />
          ))}
        </div>
      </Section>

      <Section
        title={
          <>
            <ScaleIcon width={24} height={24} />{' '}
            {t('strategicTerritoriesWidget.territoryDetail.thePillars')}
          </>
        }
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {territory.pillars.map((item) => (
            <SimpleCard key={item.title} title={item.title} description={item.description} />
          ))}
        </div>
      </Section>

      {/* Audience */}
      <Section
        title={
          <>
            <UsersIcon />
            {t('strategicTerritoriesWidget.territoryDetail.audience')}
          </>
        }
      >
        <div className="rounded-3xl border border-border bg-card p-6">
          <p className="text-heading-md text-foreground">{territory.audience.portrait}</p>
        </div>
      </Section>

      {/* Validation & Measurement */}
      <Section
        title={
          <>
            <RocketIcon />{' '}
            {t('strategicTerritoriesWidget.territoryDetail.validationAndMeasurement')}
          </>
        }
      >
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {territory.validation.map((item) => (
            <SimpleCard
              key={item.title}
              title={item.title}
              description={item.description}
              className="border-transparent bg-orange-50"
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section(props: SectionProps) {
  return (
    <>
      <Separator />
      <div className={SECTION_CLASS_NAMES}>
        <p className="flex gap-2 text-body-lg font-medium text-foreground">{props.title}</p>

        <div className="pt-10 px-6">{props.children}</div>
      </div>
    </>
  );
}

function SimpleCard(props: SimpleCardProps) {
  return (
    <div
      className={twMerge(
        'flex flex-col gap-2 rounded-3xl border border-border p-6',
        props.className,
      )}
    >
      <p className="text-body-lg font-medium text-muted-foreground">{props.title}</p>
      <p className="text-body-lg text-foreground">{props.description}</p>
    </div>
  );
}
